import React, { createContext, useContext, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { jaggeryPowderProducts, jaggeryCubesProducts, allProducts, API_URL } from '../data/products';

const AppContext = createContext(null);

export function AppProvider({ children }) {
  // ── Cart / Checkout ──
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState({
    fullName: '', email: '', phone: '', address: '', city: '', state: '', pincode: ''
  });

  // ── Wishlist ──
  const [wishlist, setWishlist] = useState([]);

  // ── UI ──
  const [notification, setNotification] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  const [isContactPopupOpen, setIsContactPopupOpen] = useState(false);

  // ── Products / Filters ──
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('powder');
  const [showInStock, setShowInStock] = useState(true);
  const [showOutOfStock, setShowOutOfStock] = useState(false);
  const [priceRange, setPriceRange] = useState({ min: 0, max: 10000 });
  const [showFilters, setShowFilters] = useState(false);
  const [productPage, setProductPage] = useState(0);
  const [selectedSizes, setSelectedSizes] = useState(() => {
    const sizes = {};
    [...(jaggeryPowderProducts ?? []), ...(jaggeryCubesProducts ?? [])].forEach(p => {
      if (p.variants.length > 0) sizes[p.id] = p.variants[0].weight;
    });
    return sizes;
  });

  // ── Hero Slider ──
  const [heroIdx, setHeroIdx] = useState(0);
  const [heroPrev, setHeroPrev] = useState(null);
  const [heroDir, setHeroDir] = useState(1);
  const [heroAnimating, setHeroAnimating] = useState(false);
  const [heroProgress, setHeroProgress] = useState(0);
  const heroProg = useRef(null);
  const heroIdxRef = useRef(0);
  const heroAnimRef = useRef(false);
  heroIdxRef.current = heroIdx;
  const HERO_SLIDES_LEN = 3;

  const heroGoTo = useCallback((next) => {
    if (heroAnimRef.current) return;
    const cur = heroIdxRef.current;
    const d = next > cur ? 1 : -1;
    setHeroDir(d);
    setHeroPrev(cur);
    setHeroAnimating(true);
    heroAnimRef.current = true;
    setHeroIdx(((next % HERO_SLIDES_LEN) + HERO_SLIDES_LEN) % HERO_SLIDES_LEN);
    setHeroProgress(0);
    setTimeout(() => { setHeroPrev(null); setHeroAnimating(false); heroAnimRef.current = false; }, 600);
  }, []);

  useEffect(() => {
    setHeroProgress(0);
    const HERO_DURATION = 5000;
    const start = Date.now();
    heroProg.current = setInterval(() => {
      const p = Math.min(100, ((Date.now() - start) / HERO_DURATION) * 100);
      setHeroProgress(p);
      if (p >= 100) { clearInterval(heroProg.current); heroGoTo(heroIdxRef.current + 1); }
    }, 50);
    return () => clearInterval(heroProg.current);
  }, [heroIdx, heroGoTo]);

  // ── Carousel ──
  const [carouselHovered, setCarouselHovered] = useState(null);
  const carouselScrollRef = useRef(null);
  const carouselPausedRef = useRef(false);

  useEffect(() => {
    const el = carouselScrollRef.current;
    if (!el) return;
    let animId;
    const CARD = window.innerWidth < 768 ? 160 : 210;
    const GAP = 12;
    const ITEMS_COUNT = 9;
    const speed = 0.6;
    const singleWidth = ITEMS_COUNT * (CARD + GAP);
    // Fix 4: Pause rAF loop when tab is hidden — saves CPU/battery on mobile
    const step = () => {
      if (!carouselPausedRef.current && document.visibilityState === 'visible') {
        el.scrollLeft += speed;
        if (el.scrollLeft >= singleWidth) el.scrollLeft -= singleWidth;
      }
      animId = requestAnimationFrame(step);
    };
    animId = requestAnimationFrame(step);
    // Also cancel entirely when tab is hidden, restart when visible
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        cancelAnimationFrame(animId);
      } else {
        animId = requestAnimationFrame(step);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      cancelAnimationFrame(animId);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── Filtered products ──
  const productCategories = useMemo(() => {
    return (selectedCategory === 'powder' ? jaggeryPowderProducts : jaggeryCubesProducts) ?? [];
  }, [selectedCategory]);

  const filteredProducts = useMemo(() => {
    return (productCategories ?? []).filter(category => {
      const selectedVariant = category.variants.find(v => v.weight === selectedSizes[category.id]) || category.variants[0];
      if (!selectedVariant) return false;
      const stockMatch = (showInStock && selectedVariant.stock > 0) || (showOutOfStock && selectedVariant.stock === 0);
      const priceMatch = selectedVariant.priceINR >= priceRange.min && selectedVariant.priceINR <= priceRange.max;
      return stockMatch && priceMatch;
    });
  }, [productCategories, selectedSizes, showInStock, showOutOfStock, priceRange]);

  const PRODUCTS_PER_PAGE = 6;
  const totalPages = Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE);
  const pagedProducts = filteredProducts.slice(productPage * PRODUCTS_PER_PAGE, (productPage + 1) * PRODUCTS_PER_PAGE);

  const maxPrice = useMemo(() => {
    let max = 0;
    productCategories.forEach(cat => cat.variants.forEach(v => { if (v.priceINR > max) max = v.priceINR; }));
    return max;
  }, [productCategories]);

  const clearAllFilters = () => {
    setShowInStock(true);
    setShowOutOfStock(false);
    setPriceRange({ min: 0, max: maxPrice });
    setProductPage(0);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (showInStock) count++;
    if (showOutOfStock) count++;
    return count;
  };

  // ── Autofill checkout form when checkout opens ──
  useEffect(() => {
    if (!isCheckoutOpen) return;

    const savedUser = (() => {
      try { return JSON.parse(localStorage.getItem('gudy_user') || 'null'); } catch { return null; }
    })();

    const savedProfiles = (() => {
      try { return JSON.parse(localStorage.getItem('gudy_checkout_profiles') || '[]'); } catch { return []; }
    })();

    // ── Helper: apply a profile object to the form (always overwrites) ──
    const applyProfile = (p) => {
      setCheckoutForm({
        fullName: p.fullName || '',
        email:    p.email    || '',
        phone:    p.phone    || '',
        address:  p.address  || '',
        city:     p.city     || '',
        state:    p.state    || '',
        pincode:  p.pincode  || '',
      });
    };

    // ── Case 1: Logged-in user with token → fetch latest order address ──
    const token = localStorage.getItem('gudy_token');
    if (savedUser && token) {
      // Fill name + email immediately while fetch loads
      setCheckoutForm({
        fullName: savedUser.name  || '',
        email:    savedUser.email || '',
        phone: '', address: '', city: '', state: '', pincode: '',
      });
      fetch(`${API_URL}/auth/orders`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          const a = data?.orders?.[0]?.shippingAddress;
          if (a) {
            applyProfile({
              fullName: savedUser.name  || a.fullName || '',
              email:    savedUser.email || a.email    || '',
              phone:    a.phone    || '',
              address:  a.address  || '',
              city:     a.city     || '',
              state:    a.state    || '',
              pincode:  a.pincode  || '',
            });
          }
        })
        .catch(() => {});
      return;
    }

    // ── Case 2: Logged-in but no token → fill name + email only ──
    if (savedUser) {
      setCheckoutForm({
        fullName: savedUser.name  || '',
        email:    savedUser.email || '',
        phone: '', address: '', city: '', state: '', pincode: '',
      });
      return;
    }

    // ── Case 3: Guest with saved profile → fill everything ──
    if (savedProfiles.length > 0) {
      applyProfile(savedProfiles[0]);
      return;
    }

    // ── Case 4: No data → clear form ──
    setCheckoutForm({ fullName: '', email: '', phone: '', address: '', city: '', state: '', pincode: '' });
  }, [isCheckoutOpen]);

  // ── Order Confirmation ──
  const [confirmedOrder, setConfirmedOrder] = useState(null); // { id, totalAmount, items }
  const user = null;
  const handleLogout = () => {};
  const authLoading = false;
  const isAuthModalOpen = false;
  const setIsAuthModalOpen = () => {};
  const authMode = 'login';
  const setAuthMode = () => {};
  const authForm = { name: '', email: '', password: '', confirmPassword: '' };
  const setAuthForm = () => {};
  const authError = '';
  const handleAuth = (e) => { e && e.preventDefault(); };

  // ── Cart sync (no-op for guests) ──
  const syncCart = async () => {};

  // ── Cart ──
  const showNotification = (msg) => {
    setNotification(msg);
    setTimeout(() => setNotification(null), 3000);
  };

  // ── Free gift: 1 free 250g jar for any 750g product ──
  const FREE_GIFT_ID = '__free-gift-250g__';
  const is750g = (item) => item.name?.toLowerCase().includes('750');

  const applyFreeGift = (cartItems) => {
    const has750 = cartItems.some(item => item.id !== FREE_GIFT_ID && is750g(item));
    const withoutGift = cartItems.filter(item => item.id !== FREE_GIFT_ID);
    if (!has750) return withoutGift;
    const freeGiftItem = {
      id: FREE_GIFT_ID,
      name: 'Free Gift – Jaggery Powder Jar 250g',
      weight: '250g',
      priceINR: 0,
      originalPrice: 0,
      image: 'https://res.cloudinary.com/dzhwcv2tr/image/upload/v1773496698/250_gm_i4wn89.png',
      quantity: 1,
      isFreeGift: true,
      freeGift: { label: 'Free 250g Jaggery Powder Jar' },
    };
    return [...withoutGift, freeGiftItem];
  };

  const addToCart = (product) => {
    const size = selectedSizes[product.id] || product.variants[0]?.weight;
    const variant = product.variants.find(v => v.weight === size) || product.variants[0];
    if (!variant) { showNotification('Product variant not available'); return; }
    const item = {
      id: `${product.id}-${size}`,
      name: product.name,
      weight: size,
      priceINR: variant.priceINR,
      originalPrice: variant.originalPrice,
      image: product.image,
      quantity: 1
    };
    const existing = cart.find(c => c.id === item.id);
    const base = existing
      ? cart.map(c => c.id === item.id ? { ...c, quantity: c.quantity + 1 } : c)
      : [...cart, item];
    const newCart = applyFreeGift(base);
    setCart(newCart);
    syncCart(newCart);
    setIsCartOpen(true);
  };

  const updateQuantity = (id, change) => {
    if (id === FREE_GIFT_ID) return; // free gift quantity is always fixed at 1
    const base = cart.map(item => {
      if (item.id === id) {
        const newQty = item.quantity + change;
        return newQty <= 0 ? null : { ...item, quantity: newQty };
      }
      return item;
    }).filter(Boolean);
    const newCart = applyFreeGift(base);
    setCart(newCart);
    syncCart(newCart);
  };

  const removeFromCart = (id) => {
    if (id === FREE_GIFT_ID) return; // can't remove free gift manually
    const base = cart.filter(item => item.id !== id);
    const newCart = applyFreeGift(base);
    setCart(newCart);
    syncCart(newCart);
    showNotification('Removed');
  };

  const toggleWishlist = (id) => {
    setWishlist(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const getTotal = () => cart.reduce((sum, item) => sum + item.priceINR * item.quantity, 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const handleCheckout = () => {
    if (cart.length === 0) { showNotification('Cart is empty'); return; }
    setIsCartOpen(false);
    setIsCheckoutOpen(true);
  };

  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('gudy_token') || null;
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const orderItems = [...cart];
      const orderTotal = getTotal();

      // ── Read directly from DOM so browser-autofilled values are captured ──
      const f = e.target;
      const getValue = (id) => (f.querySelector(`#${id}`) || {}).value || '';
      const firstN  = getValue('checkout-firstname');
      const lastN   = getValue('checkout-lastname');
      const shipping = {
        fullName: checkoutForm.fullName || (firstN + ' ' + lastN).trim() || '',
        email:    getValue('checkout-email')   || checkoutForm.email   || '',
        phone:    getValue('checkout-phone')   || checkoutForm.phone   || '',
        address:  getValue('checkout-address') || checkoutForm.address || '',
        city:     getValue('checkout-city')    || checkoutForm.city    || '',
        state:    checkoutForm.state           || '',
        pincode:  getValue('checkout-pincode') || checkoutForm.pincode || '',
      };

      const res = await fetch(`${API_URL}/orders`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          items: orderItems,
          shippingAddress: shipping,
          paymentMethod: 'cod',
          totalAmount: orderTotal
        })
      });
      if (res.ok) {
        const data = await res.json();
        const orderId = data?.order?.id || data?.id || data?.orderId || Math.floor(Math.random() * 90000) + 10000;

        // ── Save checkout profile for future autofill ──
        try {
          const existing = JSON.parse(localStorage.getItem('gudy_checkout_profiles') || '[]');
          const filtered = existing.filter(p =>
            p.email?.toLowerCase() !== shipping.email?.toLowerCase() &&
            p.phone?.trim() !== shipping.phone?.trim()
          );
          localStorage.setItem('gudy_checkout_profiles', JSON.stringify([shipping, ...filtered].slice(0, 5)));
        } catch {}

        setIsCheckoutOpen(false);
        setCart([]);
        syncCart([]);
        setCheckoutForm({ fullName: '', email: '', phone: '', address: '', city: '', state: '', pincode: '' });
        setConfirmedOrder({ id: orderId, totalAmount: orderTotal + 50, items: orderItems, name: checkoutForm.fullName });
      } else {
        showNotification('Order failed. Please try again.');
      }
    } catch (e) { showNotification('Order failed'); }
  };

  // ── Contact form ──
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);

  return (
    <AppContext.Provider value={{
      // Auth stubs (login removed — kept for backward compat)
      user, handleLogout, authLoading,
      isAuthModalOpen, setIsAuthModalOpen,
      authMode, setAuthMode,
      authForm, setAuthForm,
      authError, handleAuth,
      // Cart
      cart, isCartOpen, setIsCartOpen, isCheckoutOpen, setIsCheckoutOpen,
      checkoutForm, setCheckoutForm, addToCart, updateQuantity, removeFromCart,
      getTotal, cartCount, handleCheckout, handlePlaceOrder,
      confirmedOrder, setConfirmedOrder,
      // Wishlist
      wishlist, toggleWishlist,
      // UI
      notification, showNotification, isMobile, isChatbotOpen, setIsChatbotOpen,
      isContactPopupOpen, setIsContactPopupOpen,
      // Products
      searchQuery, setSearchQuery, selectedCategory, setSelectedCategory,
      selectedSizes, setSelectedSizes, showInStock, setShowInStock,
      showOutOfStock, setShowOutOfStock, priceRange, setPriceRange,
      showFilters, setShowFilters, productPage, setProductPage,
      productCategories, filteredProducts, pagedProducts, totalPages,
      maxPrice, clearAllFilters, getActiveFilterCount,
      // Hero
      heroIdx, heroPrev, heroDir, heroAnimating, heroProgress,
      heroProg, heroIdxRef, heroAnimRef, heroGoTo,
      // Carousel
      carouselHovered, setCarouselHovered, carouselScrollRef, carouselPausedRef,
      // Contact form
      contactForm, setContactForm, contactSubmitted, setContactSubmitted,
      contactLoading, setContactLoading,
      // API
      API_URL,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  return useContext(AppContext);
}